/**
 * /aprovafeo-orto — Curso preparatório para prova de título COFFITO (Traumato-Ortopedia).
 * Reescreve os textos da LP RD original em uma página estática mais rápida e com
 * captura via pop-up antes de liberar o link do grupo de WhatsApp.
 */
import { useTeamNumbers } from "@/lib/team-numbers";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  CheckCircle2,
  MessageCircle,
  Search,
  AlertTriangle,
} from "lucide-react";

import { MinimalFooter } from "@/components/feo/MinimalFooter";
import { AprovafeoGrupoDialog } from "@/components/aprovafeo/AprovafeoGrupoDialog";
import logoFeo from "@/assets/feo/logo-feo.png";
import logoAprovafeo from "@/assets/aprovafeo/logo-aprovafeo.png.asset.json";
import logoAprovafeoHero from "@/assets/aprovafeo/logo-aprovafeo-hero.png.asset.json";
import bannerOrto from "@/assets/aprovafeo/aprovafeo-orto-banner-nova.webp.asset.json";
import fotoAreolino from "@/assets/aprovafeo/areolino-matos.webp.asset.json";
import fotoLeandro from "@/assets/aprovafeo/leandro-fukusawa.webp.asset.json";
import depoimento1 from "@/assets/aprovafeo/depoimento-1.png.asset.json";
import depoimento2 from "@/assets/aprovafeo/depoimento-2.png.asset.json";
import depoimentoDestaque from "@/assets/aprovafeo/depoimento-destaque.png.asset.json";
import iconStar from "@/assets/aprovafeo/icons/star-v4.png.asset.json";
import iconCalendar from "@/assets/aprovafeo/icons/calendar-v4.png.asset.json";
import iconChecklist from "@/assets/aprovafeo/icons/checklist-v4.png.asset.json";
import iconBadge from "@/assets/aprovafeo/icons/badge-v4.png.asset.json";
import iconGrupo from "@/assets/aprovafeo/icons/grupo-v4.png.asset.json";
import iconPlay from "@/assets/aprovafeo/icons/play-v4.png.asset.json";
import fundoAprovafeo from "@/assets/aprovafeo/fundo-aprovafeo.jpg.asset.json";
import seloGarantia from "@/assets/SELO_APROVAFEO.webp.asset.json";
import logoStarCursos from "@/assets/revista/star-cursos-logo.png.asset.json";
import mockupStarCursos from "@/assets/revista-star/mockup-star-cursos.png.asset.json";

// Cores oficiais AprovaFEO (logo) + apoio (referência criativa)
const BRAND = {
  navy: "#1A2058",
  navyDeep: "#0F1440",
  sky: "#7BB8FF",
  mint: "#4FE3B8",
  yellow: "#F5D93A",
  pink: "#F9C8D4",
  lightBlue: "#B8DFF9",
  softMint: "#8AE0C4",
};

const GRUPO_WPP_LINK = "https://nimble.li/v9alpbjm";


export const Route = createFileRoute("/aprovafeo-orto-bkp")({
  head: () => ({
    meta: [
      { title: "AprovaFEO Traumato-Ortopedia · backup do lançamento (grupo de WhatsApp)" },
      {
        name: "description",
        content:
          "Versão arquivada da LP do AprovaFEO Traumato-Ortopedia (lançamento com grupo de WhatsApp). Página interna, não indexada.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "AprovaFEO Traumato-Ortopedia · backup do lançamento (grupo de WhatsApp)" },
      {
        property: "og:description",
        content: "Versão arquivada da LP do AprovaFEO. Página interna, não indexada.",
      },
      { property: "og:type", content: "website" },
    ],
  }),
  component: AprovafeoOrtoPage,
});

const BENEFITS = [
  "Organize o seu plano de estudos",
  "Ganhe confiança com simulados e revisões",
  "Participe de encontros ao vivo para tirar dúvidas",
];

const SOLUCAO = [
  { titulo: "Plano de estudos", texto: "Organize a sua preparação com um cronograma de estudos estruturado para a prova.", bg: "#B8DFF9" },
  { titulo: "Simulados", texto: "Teste seus conhecimentos com simulados e análise de provas anteriores.", bg: "#8AE0C4" },
  { titulo: "Acompanhamento 360º", texto: "Tenha o suporte dos professores, esclareça suas dúvidas e mantenha o foco no que realmente importa.", bg: "#F5D93A" },
  { titulo: "Estratégia", texto: "Estude com método, foco e direcionamento para a prova de Especialidade em Traumato-Ortopedia.", bg: "#F9C8D4" },
];

const CONTEUDO = [
  { icon: iconStar.url, titulo: "3 anos de Star Cursos", texto: "Reforce seus conhecimentos com +120 cursos na área da fisioterapia musculoesquelética.", bg: "#7BB8FF" },
  { icon: iconCalendar.url, titulo: "Combo Imersões FEO", texto: "As 7 edições das nossas Imersões de Verão e Inverno, com aulas ministradas por grandes referências da fisioterapia.", bg: "#7BB8FF" },
  { icon: iconChecklist.url, titulo: "Simulados Direcionados", texto: "Simulados focados no formato da prova COFFITO/ABRAFITO para você chegar tranquilo no seu dia.", bg: "#7BB8FF" },
  { icon: iconBadge.url, titulo: "Análise de Títulos", texto: "Orientação completa sobre como organizar e apresentar seus títulos para máxima pontuação.", bg: "#7BB8FF" },
  { icon: iconGrupo.url, titulo: "Grupo Exclusivo", texto: "Grupo no WhatsApp com mentor disponível para dúvidas e discussões entre participantes.", bg: "#7BB8FF" },
  { icon: iconPlay.url, titulo: "Encontros Ao Vivo", texto: "Lives para tirar dúvidas e revisar conteúdos com os mentores do curso.", bg: "#7BB8FF" },
];

const FAQ = [
  {
    q: "Como eu faço a minha inscrição?",
    a: "As inscrições serão abertas em breve. Faça sua pré-inscrição nesta página e entre no grupo oficial do WhatsApp para receber todas as informações em primeira mão e conhecer a condição especial de lançamento.",
  },
  {
    q: "Por que entrar no grupo do WhatsApp?",
    a: "O grupo oficial será o principal canal de comunicação do lançamento. É por lá que vamos divulgar a oferta especial, avisar sobre a abertura das inscrições e compartilhar todas as novidades do APROVA FEO.",
  },
  {
    q: "Preciso esperar o edital para começar a estudar?",
    a: "Não. Quanto antes você organizar seus estudos, mais tempo terá para revisar os principais conteúdos e chegar com mais confiança ao dia da prova.",
  },
  {
    q: "Por quanto tempo terei acesso ao conteúdo?",
    a: "Sabemos que cada fisioterapeuta tem o seu próprio ritmo de preparação. Por isso, o APROVA FEO fica disponível por 2 anos, para que você possa revisar as aulas, refazer simulados e continuar estudando caso a aprovação não aconteça na primeira tentativa. Assim, você segue preparado para as próximas edições da prova de Especialidade do COFFITO.",
  },
  {
    q: "O curso é 100% online?",
    a: "Sim. Todas as aulas, materiais, simulados e encontros ao vivo serão realizados online, para que você possa participar de qualquer lugar, no seu ritmo e com total flexibilidade. Não há necessidade de deslocamento ou gastos extras com viagens.",
  },
  {
    q: "O curso também inclui conteúdo técnico da área?",
    a: "O AprovaFEO não é um curso de conteúdo técnico, ele é um preparatório estratégico para a prova de título. Mas você não vai ficar desamparado: ao se inscrever, ganha 3 anos gratuitos da Star Cursos, nossa plataforma com mais de 100 cursos de fisioterapia musculoesquelética. Dentro do AprovaFEO, vamos indicar exatamente quais desses cursos valem a pena para reforçar sua preparação.",
  },
  {
    q: "O curso garante minha aprovação?",
    a: "Não existe garantia de aprovação, isso depende do seu comprometimento e da aplicação do que será ensinado. O que entregamos é um método claro, ferramentas práticas e acompanhamento especializado para que você tenha as melhores condições possíveis de atingir a pontuação mínima e conquistar o título.",
  },
];

function formatBrPhone(raw: string) {
  const v = raw.replace(/\D/g, "").slice(0, 11);
  if (v.length > 6) return v.replace(/(\d{2})(\d{5})(\d+)/, "($1) $2-$3");
  if (v.length > 2) return v.replace(/(\d{2})(\d+)/, "($1) $2");
  return v;
}

function AprovafeoOrtoPage() {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#1A2058] text-white overflow-x-hidden">
      {/* WRAPPER HERO + SEÇÃO 2 — fundo contínuo sem divisão */}
      <div className="relative overflow-hidden">
        {/* Background image + premium overlays compartilhados */}
        <div
          aria-hidden
          className="absolute inset-0 scale-105 bg-cover bg-center"
          style={{ backgroundImage: `url(${fundoAprovafeo.url})` }}
        />
        <div aria-hidden className="absolute inset-0 bg-[#0F1440]/45" />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-[#1A2058]/30 via-transparent to-[#0F1440]/50" />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background: "radial-gradient(circle at 50% 0%, rgba(123,184,255,0.32) 0%, transparent 55%)",
          }}
        />
        <div aria-hidden className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-[#0F1440] to-transparent opacity-60" />
        <div aria-hidden className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#0F1440] to-transparent" />

        {/* HERO */}
        <section className="relative flex min-h-[92vh] items-center justify-center overflow-hidden">


        <div className="relative z-10 container mx-auto max-w-4xl px-5 sm:px-6 py-16 sm:py-24 text-center">
          <div className="mx-auto max-w-3xl rounded-[2rem] border border-[#7BB8FF]/30 bg-white/[0.08] p-6 sm:p-10 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.55)] backdrop-blur-2xl">
            <img decoding="async"
              src={logoAprovafeo.url}
              alt="AprovaFEO"
              className="mx-auto mb-5 h-14 sm:h-18 w-auto drop-shadow-[0_4px_20px_rgba(0,0,0,0.35)]"
            />
            <span className="inline-block rounded-full bg-[#4FE3B8]/15 border border-[#4FE3B8]/30 px-4 py-1.5 text-[0.7rem] sm:text-xs font-extrabold uppercase tracking-[0.18em] text-[#4FE3B8]">
              Traumato-Ortopedia
            </span>
            <h1 className="mx-auto mt-6 max-w-3xl text-[clamp(1.9rem,6vw,3.5rem)] font-extrabold leading-[1.05] tracking-tight text-white">
              Curso preparatório para prova de Especialidade do <span className="text-[#7BB8FF]">COFFITO</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base sm:text-xl font-medium text-white/80">
              Prepare-se para a Avaliação de Especialidade com estratégia, simulados e mentoria de quem já trilhou o caminho.
            </p>

            <ul className="mx-auto mt-8 grid max-w-xl gap-3 text-left sm:text-center">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start justify-start sm:justify-center gap-2 text-sm sm:text-base text-white/90">
                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#4FE3B8]/15 text-[#4FE3B8]">
                    <CheckCircle2 className="h-3 w-3" />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10">
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 sm:px-9 py-4 text-sm sm:text-base font-bold text-white transition-all hover:scale-[1.03]"
              >
                <MessageCircle className="h-5 w-5" /> Entrar no grupo do WhatsApp
              </button>
            </div>

            <div className="mt-6 flex flex-col items-center justify-center gap-3 text-[0.65rem] sm:text-xs font-semibold uppercase tracking-wider text-white/50 sm:flex-row sm:flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#4FE3B8]" /> 100% ONLINE
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#7BB8FF]" /> ENCONTROS AO VIVO
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#F5D93A]" /> BÔNUS ESPECIAIS
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* SEÇÃO 2 — DEPOIMENTOS REAIS DO GRUPO */}
      <WhatsAppTestimonialsSection />

        <section className="relative container mx-auto max-w-6xl px-5 sm:px-6 py-14 sm:py-20">
          <div className="grid gap-8 md:grid-cols-2 md:items-stretch">
            <div className="flex h-full min-h-0 items-center justify-center overflow-hidden rounded-2xl border border-white/10 shadow-lg shadow-black/30">
              <img
                src={bannerOrto.url}
                alt="AprovaFEO, Traumato-Ortopedia"
                loading="lazy"
                className="h-auto max-h-full w-full object-cover"
              />
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-2xl sm:text-4xl lg:text-[2.5rem] font-extrabold leading-tight w-full max-w-[360px] mx-auto sm:max-w-none sm:mx-0">
                Você ainda mais perto do seu título de especialista
              </h2>
              <div className="mt-6 space-y-4 text-[0.94rem] sm:text-base text-white/75 leading-relaxed">
                <p>
                  O <strong>APROVA FEO</strong> é um curso preparatório 100% online para a prova de título de especialidade em Traumato-Ortopedia do COFFITO.
                </p>
                <p>
                  Mais do que oferecer conteúdo técnico, o curso ajuda você a organizar a sua preparação e a direcionar os estudos para o que realmente importa, no tempo certo e com a estratégia certa.
                </p>
                <p>
                  Sua preparação não acontece sozinho. Durante toda a jornada, você terá acesso a aulas direcionadas, materiais de apoio, simulados, revisões e fará parte de um grupo exclusivo com os professores. É por lá que vamos acompanhar a sua evolução, esclarecer dúvidas, compartilhar orientações importantes sobre a prova e construir essa preparação junto com você e os demais alunos.
                </p>
              </div>
            </div>
          </div>
        </section>


        {/* PROBLEMA / SOLUÇÃO */}
        <section className="relative">
        <div className="mx-auto max-w-[92rem] px-4 sm:px-6 lg:px-8 py-10 sm:py-14 pb-20 sm:pb-28">
          <div className="mx-auto max-w-2xl rounded-2xl border border-white/10 bg-[#F5C518]/5 p-6 sm:p-8 text-center">
            <p className="text-xs font-bold uppercase tracking-widest text-[#7BB8FF]">O problema</p>
            <p className="mt-3 text-lg sm:text-2xl font-bold leading-snug">
              Você quer seu título e já tem o conhecimento técnico, mas <span className="text-[#7BB8FF]">falta estratégia para a prova.</span>
            </p>
          </div>

          <div className="mt-16 text-center">
            <p className="text-xs font-bold uppercase tracking-widest text-[#4FE3B8]">A solução</p>
            <h3 className="mt-2 text-2xl sm:text-3xl font-extrabold">O caminho completo para a sua aprovação</h3>
          </div>

          <div className="mx-auto mt-10 grid w-full grid-cols-1 items-stretch gap-6 sm:grid-cols-2 xl:grid-cols-4 xl:gap-8">
            {SOLUCAO.map(({ titulo, texto, bg }, i) => (
              <div
                key={titulo}
                className="group relative flex h-full min-w-0 flex-col overflow-visible rounded-[1.65rem] border border-white/[0.24] p-8 sm:p-9 lg:p-10 xl:min-h-[14rem] xl:p-8 shadow-[0_24px_80px_-22px_rgba(0,0,0,0.38)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_34px_95px_-24px_rgba(0,0,0,0.46)]"
                style={{ backgroundColor: bg }}
              >
                {/* Premium inner glow overlay */}
                <div
                  className="pointer-events-none absolute inset-0 rounded-[1.5rem] opacity-25 transition-opacity group-hover:opacity-40"
                  style={{ background: `linear-gradient(135deg, rgba(255,255,255,0.75) 0%, transparent 60%)` }}
                />

                {/* Arrow: down on mobile, right on desktop */}
                {i < SOLUCAO.length - 1 && (
                  <>
                    <span
                      aria-hidden
                      className="absolute left-1/2 -bottom-3 z-10 h-6 w-6 -translate-x-1/2 rotate-45 border-r border-b border-white/[0.22] lg:hidden"
                      style={{ backgroundColor: bg }}
                    />
                    <span
                      aria-hidden
                      className="absolute -right-4 top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 rotate-45 border-r border-t border-white/[0.22] xl:block"
                      style={{ backgroundColor: bg }}
                    />
                  </>
                )}

                <h4 className="relative z-10 whitespace-nowrap text-base font-extrabold leading-tight tracking-tight text-[#1A2058] sm:text-[1.05rem] xl:text-[1rem] 2xl:text-[1.08rem]">
                  {titulo}
                </h4>
                <p className="relative z-10 mt-5 flex-1 text-base font-medium leading-relaxed text-[#1A2058]/85">
                  {texto}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      </div>

      {/* DEPOIMENTOS + DIFERENCIAL */}
      <section className="border-t border-white/10 bg-gradient-to-b from-[#7c5cff]/20 via-[#5a62ff]/10 to-[#7c5cff]/20">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6 py-14 sm:py-20">
          {/* Depoimento em destaque especial — iluminação roxa */}
          <div className="mx-auto mb-12 max-w-4xl">
            <div className="relative">
              {/* Glow externo */}
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-8 rounded-[2rem] blur-2xl"
                style={{
                  background:
                    "radial-gradient(70% 70% at 50% 50%, rgba(123,184,255,0.6) 0%, rgba(123,184,255,0.2) 55%, transparent 80%)",
                }}
              />
              <div
                className="relative rounded-[1.75rem] p-[2px] transition-transform duration-300 hover:scale-[1.01]"
                style={{
                  background:
                    "linear-gradient(135deg, #7BB8FF 0%, rgba(123,184,255,0.35) 50%, #7BB8FF 100%)",
                  boxShadow:
                    "0 0 0 1px rgba(123,184,255,0.45), 0 40px 100px -20px rgba(123,184,255,0.65)",
                }}
              >
                <img
                  src={depoimentoDestaque.url}
                  alt="Depoimento em destaque de aluna aprovada no Título de Especialista"
                  loading="lazy"
                  className="block h-auto w-full rounded-[1.65rem] shadow-lg"
                />
              </div>
            </div>
          </div>

          <div className="text-center">
            <h2 className="text-2xl sm:text-4xl font-extrabold leading-tight">
              Saber apenas fisioterapia <span className="text-[#7BB8FF]">não é o suficiente.</span>
            </h2>
            <p className="mt-4 text-xl sm:text-2xl text-white/80">
              Você precisa dominar todas as etapas do processo.
            </p>
          </div>



          <ul className="mx-auto mt-10 max-w-lg space-y-3 text-left text-sm sm:text-base text-white/90">
            {[
              "Como funciona a inscrição e o envio da documentação.",
              "Entendimento do edital e dos critérios da prova.",
              "Como funciona a pontuação e a análise do currículo.",
              "Dicas práticas para o dia da prova.",
              "Apoio na elaboração de recursos administrativos.",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#4FE3B8]" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CONTEÚDO / O QUE VOCÊ TERÁ */}
      <section className="container mx-auto max-w-5xl px-5 sm:px-6 py-14 sm:py-20">
        <div className="text-center">
          <h2 className="text-2xl sm:text-4xl font-extrabold leading-tight">O que você terá acesso no <span className="text-[#7BB8FF]">APROVA FEO</span></h2>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CONTEUDO.map(({ icon, titulo, texto, bg }) => (
            <div key={titulo} className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-xl p-3" style={{ backgroundColor: bg }}>
                <img src={icon} alt="" loading="lazy" className="h-11 w-11 object-contain" />
              </div>
              <h4 className="mt-4 text-base font-bold text-white">{titulo}</h4>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">{texto}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA INTERMEDIÁRIO */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#8FC4FF] via-[#7BB8FF] to-[#6BAEF7] px-6 sm:px-12 py-14 sm:py-20 text-center shadow-[0_30px_80px_-20px_rgba(26,32,88,0.35)]">
          {/* Faixas decorativas premium */}
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-white/20 via-transparent to-white/5" />
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-white/15 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#1A2058]/10 blur-3xl" />

          <div className="relative z-10 mx-auto max-w-3xl">
            <img
              src={logoAprovafeoHero.url}
              alt="AprovaFEO"
              loading="eager"
              className="mx-auto mb-4 h-[2.1rem] sm:h-[3rem] w-auto drop-shadow-[0_4px_12px_rgba(26,32,88,0.25)]"
            />
            
            <h2 className="mt-3 text-xl sm:text-4xl font-extrabold leading-tight whitespace-nowrap text-[#1A2058]">
              Faça parte da próxima turma!
            </h2>
            <p className="mt-4 text-sm sm:text-base text-[#1A2058]/85 leading-relaxed whitespace-pre-line">
              Faça sua pré-inscrição e entre no grupo oficial do WhatsApp.

              {"\n\n"}
              <span className="text-[#0F1440] font-semibold">É exclusivamente por lá</span> que vamos revelar a oferta especial de lançamento, compartilhar todas as informações sobre o curso e avisar quando as inscrições forem abertas.
            </p>
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="mt-8 inline-flex items-center gap-1.5 sm:gap-2 whitespace-nowrap rounded-full bg-[#25D366] px-5 sm:px-10 py-3.5 sm:py-5 text-sm sm:text-lg font-bold text-white shadow-lg shadow-[#1A2058]/20 transition-transform hover:scale-[1.03]"
            >
              <MessageCircle className="h-5 w-5 shrink-0 sm:h-6 sm:w-6" /> Entrar no grupo do WhatsApp
            </button>
          </div>
        </div>
      </section>

      {/* MENTORES */}
      <section className="container mx-auto max-w-4xl px-5 sm:px-6 py-14 sm:py-20">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-[#7BB8FF]">Quem serão os seus mentores?</p>
        </div>
        <div className="mt-10 space-y-10">
          {/* Areolino */}
          <div className="grid gap-6 sm:grid-cols-[240px_1fr] items-stretch">
            <img
              src={fotoAreolino.url}
              alt="Areolino Matos, mentor do AprovaFEO"
              loading="lazy"
              className="mx-auto w-full max-w-[240px] rounded-2xl border border-white/10 object-cover object-top shadow-lg shadow-black/30 sm:max-w-none h-full min-h-[260px]"
            />
            <div className="text-center sm:text-left flex flex-col justify-center">
              <h3 className="text-xl sm:text-2xl font-extrabold">Areolino Matos</h3>
              <div className="mt-4 space-y-3 text-xs sm:text-sm text-white/75 leading-relaxed">
                <p>
                  <strong>Fisioterapeuta</strong>, <strong>mestre</strong> em Reabilitação pela Universidade Federal de São Paulo (UNIFESP) e <strong>doutor</strong> em Engenharia Biomédica pela Universidade Camilo Castelo Branco (UNICASTELO).
                </p>
                <p>
                  Professor do Programa de Pós-Graduação em Ciências da Saúde da Universidade Federal do Amapá (UNIFAP), onde também lidera o Grupo de Pesquisa em Saúde Baseada em Evidências (GESBE).
                </p>
                <p>
                  Membro da ABRAFITO, pesquisador colaborador do GepSide/UFTM e colaborador da iniciativa Choosing Wisely Brasil na área de fisioterapia musculoesquelética.
                </p>
              </div>
            </div>
          </div>
          {/* Leandro */}
          <div className="grid gap-6 sm:grid-cols-[240px_1fr] items-stretch">
            <img
              src={fotoLeandro.url}
              alt="Leandro Fukusawa, mentor do AprovaFEO"
              loading="lazy"
              className="mx-auto w-full max-w-[240px] rounded-2xl border border-white/10 object-cover object-top shadow-lg shadow-black/30 sm:max-w-none h-full min-h-[260px]"
            />
            <div className="text-center sm:text-left flex flex-col justify-center">
              <h3 className="text-xl sm:text-2xl font-extrabold">Leandro Fukusawa</h3>
              <div className="mt-4 space-y-3 text-xs sm:text-sm text-white/75 leading-relaxed">
                <p>
                  Fisioterapeuta com ampla experiência clínica nas áreas de ortopedia, traumatologia e esporte. Especialista em reabilitação musculoesquelética pela FCMSC-SP, mestre em Fisioterapia pela UNICID e atualmente doutorando no programa de Ciências da Saúde pela FCMSC-SP.
                </p>
                <p>
                  Foi fisioterapeuta da Seleção de Futebol Feminino entre 2015 e 2019 e passou por clínicas de referência em São Paulo (GERF, Instituto Nichioka, entre outras).
                </p>
                <p>
                  Fundador do projeto social MOVA-SE+ (Fisioterapia em Apoio ao Artista), fisioterapeuta na companhia Cisne Negro há 10 anos, Diretor de Produto e Co-Fundador do Portal Fisio em Ortopedia e Responsável Técnico na Tato.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STAR CURSOS — INCLUSO NO APROVA FEO */}
      <section className="border-t border-white/10 bg-white/[0.02]">
        <div className="container mx-auto max-w-5xl px-5 sm:px-6 py-14 sm:py-20">
          <div className="mx-auto max-w-5xl text-center">
            <h2 className="mt-3 text-center text-xl sm:text-2xl lg:text-3xl font-extrabold leading-tight sm:whitespace-nowrap">
              E se bater a insegurança no conhecimento teórico?
            </h2>
            <p className="mx-auto mt-4 max-w-[90ch] text-base sm:text-lg text-white/70 leading-relaxed">
              Não se preocupe. Você também terá acesso à <strong className="text-white">Star Cursos</strong>, nossa plataforma com mais de <strong className="text-white">120 cursos</strong>, para revisar conteúdos, aprofundar temas específicos e fortalecer sua preparação para a prova.
            </p>
          </div>

          {/* Card destacado */}
          <div className="relative mx-auto mt-8 max-w-3xl overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0F1440] via-[#131a5e] to-[#1A2058] p-1 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)]">
            {/* Glow de destaque */}
            <div
              aria-hidden
              className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#00b899]/20 blur-[100px]"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -left-20 bottom-0 h-64 w-64 rounded-full bg-[#00c9d6]/15 blur-[100px]"
            />

            <div className="relative grid grid-cols-1 items-center gap-6 px-6 py-7 sm:px-10 sm:pt-10 sm:pb-5 sm:grid-cols-[1fr_1.65fr]">
              {/* Coluna 1 — logo, texto, box 2x2 */}
              <div className="flex w-full flex-col items-center text-center sm:items-start sm:text-left">
                <img
                  src={logoStarCursos.url}
                  alt="Star Cursos"
                  className="h-12 w-auto sm:h-14"
                  loading="lazy"
                />
                <p className="mt-5 mb-2 max-w-[32ch] text-sm text-white/70 leading-relaxed whitespace-pre-line">
                  A maior biblioteca de fisioterapia{"\n"}
                  musculoesquelética do Brasil.{"\n"}
                  Acesso incluso no AprovaFEO!
                </p>

                <div className="mt-3 grid w-full grid-cols-2 gap-2 sm:max-w-[14rem]">
                  <StarMetric number="+120" label="cursos" />
                  <StarMetric number="+15 mil" label="alunos" />
                  <StarMetric number="+50" label="professores" />
                  <StarMetric number="+350h" label="conteúdo" />
                </div>
              </div>

              {/* Coluna 2 — mockup centralizado */}
              <div className="relative flex items-center justify-center py-0 mt-5 sm:mt-8">
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="h-[75%] w-[85%] rounded-full bg-[#00b899]/25 blur-[80px]" />
                </div>
                <img
                  src={mockupStarCursos.url}
                  alt="Plataforma Star Cursos com aulas de fisioterapia"
                  className="relative z-10 w-full max-w-[1066px] drop-shadow-[0_30px_60px_rgba(0,0,0,0.35)]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GARANTIA */}
      <section className="border-t border-white/10 bg-white/[0.02]">
        <div className="container mx-auto max-w-4xl px-5 sm:px-6 py-14 sm:py-20 text-center">
          <div className="mx-auto max-w-[220px]">
            <img
              src={seloGarantia.url}
              alt="Selo de Garantia AprovaFEO"
              loading="lazy"
              className="h-auto w-full drop-shadow-[0_20px_50px_rgba(0,0,0,0.45)]"
            />
          </div>
          <h2 className="mt-6 text-lg sm:text-3xl lg:text-4xl font-extrabold leading-tight whitespace-nowrap">
            Sua preparação continua, se precisar.
          </h2>
          <div className="mt-6 space-y-4 text-sm sm:text-base text-white/75 leading-relaxed">
            <p>
              Acreditamos na qualidade da preparação que construímos e queremos que você chegue à prova com confiança.
            </p>
            <p>
              Mas sabemos que cada jornada é única. Por isso, caso a aprovação não aconteça na primeira tentativa, você continuará com acesso ao <strong>APROVA FEO</strong> e poderá acompanhar a próxima edição do preparatório, em 2027.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/10 bg-white/[0.02]">
        <div className="container mx-auto max-w-3xl px-5 sm:px-6 py-14 sm:py-20">
          <h2 className="text-center text-2xl sm:text-4xl font-extrabold">Perguntas Frequentes</h2>
          <div className="mt-8 space-y-3">
            {FAQ.map((item) => (
              <details key={item.q} className="group rounded-2xl border border-white/10 bg-white/5 p-5 open:bg-white/[0.07]">
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm sm:text-base font-bold list-none">
                  <span>{item.q}</span>
                  <span className="text-[#4FE3B8] transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL — Grupo do WhatsApp (sutil) */}
      <section className="border-t border-white/10 bg-white/[0.02]">
        <div className="container mx-auto max-w-3xl px-5 sm:px-6 py-12 sm:py-16 text-center">
          <h2 className="text-xl sm:text-4xl font-extrabold leading-tight whitespace-nowrap">
            Não fique de fora do <span className="text-[#7BB8FF]">grupo oficial</span>
          </h2>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-8 sm:px-10 py-4 sm:py-5 text-base sm:text-lg font-bold text-white shadow-lg shadow-[#1A2058]/20 transition-transform hover:scale-[1.03]"
          >
            <MessageCircle className="h-6 w-6" /> Entrar no grupo do WhatsApp
          </button>
        </div>
      </section>

      {/* Verificador */}
      <WhatsAppSection />

      <MinimalFooter />

      <AprovafeoGrupoDialog open={dialogOpen} onOpenChange={setDialogOpen} grupoWppLink={GRUPO_WPP_LINK} />
    </div>
  );
}

function StarMetric({ number, label }: { number: string; label: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.05] px-2 py-2 text-center backdrop-blur-sm sm:px-3">
      <div className="text-sm font-black text-white sm:text-base">{number}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-white/60">{label}</div>
    </div>
  );
}

function WhatsAppSection() {
  const TEAM_NUMBERS = useTeamNumbers();
  const [value, setValue] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "fail" | "invalid">("idle");

  const verificar = () => {
    const digits = value.replace(/\D/g, "").slice(-11);
    if (digits.length < 10) { setStatus("invalid"); return; }
    setStatus("loading");
    setTimeout(() => {
      const full = "55" + digits;
      setStatus(TEAM_NUMBERS.includes(full) ? "ok" : "fail");
    }, 600);
  };

  return (
    <section className="border-t border-white/10 bg-white/[0.02]">
      <div className="container mx-auto px-5 sm:px-6 py-14 sm:py-20 text-center">
        <div className="mx-auto max-w-xl rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-8 text-center shadow-2xl">
          <div className="mb-5 flex justify-center">
            <img loading="lazy" decoding="async" src={logoFeo} alt="Fisio em Ortopedia" className="h-8 sm:h-10 w-auto" />
          </div>
          <h3 className="text-center text-base sm:text-2xl font-bold text-white">Verifique o número do nosso time</h3>
          <p className="mt-2 text-center text-[0.8125rem] sm:text-sm text-white/60">
            Digite o número e confirme se <span className="block sm:hidden" /> pertence à nossa equipe
          </p>
          <div className="mt-5 flex flex-col sm:flex-row items-stretch gap-2 rounded-xl bg-white/5 p-2 shadow-inner border border-white/10">
            <div className="hidden sm:flex items-center pl-3 text-sm font-semibold text-white/50">BR</div>
            <input
              type="tel"
              inputMode="numeric"
              placeholder="(00) 00000-0000"
              value={value}
              onChange={(e) => setValue(formatBrPhone(e.target.value))}
              onKeyDown={(e) => e.key === "Enter" && verificar()}
              className="flex-1 bg-transparent px-3 py-3 text-base text-white outline-none placeholder:text-white/40"
              maxLength={16}
            />
            <button
              type="button"
              onClick={verificar}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 text-sm font-bold text-[#1A2058] transition-colors hover:bg-white/90"
            >
              <Search className="h-4 w-4" /> VERIFICAR
            </button>
          </div>
          {status === "loading" && (
            <div className="mt-4 rounded-lg bg-white/5 px-4 py-3 text-sm font-semibold text-white/70">🔎 Verificando número...</div>
          )}
          {status === "invalid" && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-400">
              <AlertTriangle className="h-4 w-4" /> Digite um número válido com DDD
            </div>
          )}
          {status === "ok" && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-[#4FE3B8]/10 px-4 py-3 text-sm font-semibold text-[#4FE3B8]">
              <CheckCircle2 className="h-4 w-4" /> Este número faz parte do time da FEO
              <span className="ml-auto rounded-full bg-[#4FE3B8] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#1A2058]">Verificado</span>
            </div>
          )}
          {status === "fail" && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-400">
              <AlertTriangle className="h-4 w-4" /> Este número NÃO pertence ao nosso time
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ==========================================================================
 * SEÇÃO 2 — Depoimentos reais do grupo (estilo WhatsApp)
 * ========================================================================== */


import depV2_1 from "@/assets/aprovafeo/wpp-v2/dep-1.png.asset.json";
import depV2_2 from "@/assets/aprovafeo/wpp-v2/dep-2.png.asset.json";
import depV2_3 from "@/assets/aprovafeo/wpp-v2/dep-3.png.asset.json";
import depV2_4 from "@/assets/aprovafeo/wpp-v2/dep-4.png.asset.json";
import depV2_5 from "@/assets/aprovafeo/wpp-v2/dep-5.png.asset.json";
import depV2_6 from "@/assets/aprovafeo/wpp-v2/dep-6.png.asset.json";

const WPP_PRINTS_V2 = [depV2_6, depV2_4, depV2_5, depV2_3, depV2_2, depV2_1];

function WhatsAppTestimonialsSection() {
  return (
    <section className="relative overflow-hidden py-6 sm:py-10 text-white">
      {/* Glow de fundo centralizado entre os depoimentos */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(65% 55% at 50% 62%, rgba(123,184,255,0.30) 0%, rgba(90,98,255,0.14) 40%, transparent 75%)",
        }}
      />
      <div className="container relative mx-auto max-w-[100rem] px-4 sm:px-6">
        <h2 className="mx-auto text-center text-2xl font-extrabold leading-tight tracking-tight sm:text-4xl md:text-[2.75rem] md:whitespace-nowrap">
          Toda conquista começa com uma <span className="text-[#7BB8FF]">boa preparação.</span>
        </h2>
        <p className="mx-auto mt-4 text-center text-sm text-white/70 sm:text-base md:text-lg md:whitespace-nowrap">
          Relatos reais de fisioterapeutas que conquistaram o Título de Especialista em 2025.
        </p>

        <div className="mt-6 grid grid-cols-1 gap-x-3 gap-y-4 sm:grid-cols-2 lg:grid-cols-2">
          {WPP_PRINTS_V2.map((item, i) => (
            <div key={i} className="flex items-center justify-center">
              <img
                src={item.url}
                alt={`Depoimento de aluno aprovado ${i + 1}`}
                loading="lazy"
                className="block h-auto w-full rounded-lg shadow-lg transition-transform duration-300 hover:scale-[1.01]"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}



